/**
 * Vaccination status classification. Mirrors backend
 * ``app.services.vaccine_service.calculate_dynamic_status``.
 *
 * The backend is the source of truth: every list call re-derives the
 * status from the row. This module exists so the table can render the
 * correct colour/label without an extra round-trip per row.
 *
 * Rules (must stay in lockstep with the backend):
 *   - if status is already "Completed" AND dateGiven is set → "Completed"
 *   - scheduled more than 14 days in the future → "Action" (UI renders as "-")
 *   - scheduled within next 14 days           → "Upcoming" (gray)
 *   - scheduled in [today-30, today)           → "Due"     (amber)
 *   - scheduled before today-30                → "Overdue" (red)
 *   - no DOB / unrecognised label              → current_status unchanged
 */
export const VACCINE_STATUS = Object.freeze({
    ACTION: 'Action',
    UPCOMING: 'Upcoming',
    DUE: 'Due',
    OVERDUE: 'Overdue',
    COMPLETED: 'Completed',
});

export const VACCINE_STATUSES = Object.freeze([
    VACCINE_STATUS.UPCOMING,
    VACCINE_STATUS.DUE,
    VACCINE_STATUS.OVERDUE,
    VACCINE_STATUS.COMPLETED,
    VACCINE_STATUS.ACTION,
]);

/** Map a status to the <StatusBadge severity="..."/> tokens. */
export function vaccineSeverity(status) {
    switch (status) {
        case VACCINE_STATUS.COMPLETED:
            return 'success';
        case VACCINE_STATUS.UPCOMING:
            return 'info';
        case VACCINE_STATUS.DUE:
            return 'warning';
        case VACCINE_STATUS.OVERDUE:
            return 'danger';
        case VACCINE_STATUS.ACTION:
            return 'neutral';
        default:
            return 'neutral';
    }
}

/**
 * Compute the live status of a vaccine row given the child's DOB.
 * Mirrors the backend exactly — see comments at top of file.
 *
 * @param {string|null|undefined} dob       ISO date (yyyy-mm-dd)
 * @param {string} ageLabel                 e.g. "2 Months Completed"
 * @param {string|null} currentStatus       DB-stored status (may be null)
 * @param {string|null} dateGiven           ISO date if vaccine was administered
 * @param {Date}   [today]                  override for tests
 */
export function computeDynamicStatus(
    dob,
    ageLabel,
    currentStatus,
    dateGiven,
    today = new Date(),
) {
    if (currentStatus === VACCINE_STATUS.COMPLETED && dateGiven) {
        return VACCINE_STATUS.COMPLETED;
    }
    if (!dob || !ageLabel) {
        return currentStatus || VACCINE_STATUS.ACTION;
    }
    const scheduled = computeScheduledDate(dob, ageLabel);
    if (!scheduled) {
        return currentStatus || VACCINE_STATUS.ACTION;
    }
    const diffDays = Math.floor((scheduled.getTime() - toMidnight(today).getTime()) / 86400000);
    if (diffDays > 14) return VACCINE_STATUS.ACTION;
    if (diffDays >= 0) return VACCINE_STATUS.UPCOMING;
    if (diffDays >= -30) return VACCINE_STATUS.DUE;
    return VACCINE_STATUS.OVERDUE;
}

function toMidnight(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Mirror of backend ``app.utils.dates.compute_scheduled_date``. The
 * list endpoint already stores ``date_scheduled`` per child but the FE
 * recomputes for colour/severity on each render so this stays local.
 */
export function computeScheduledDate(dob, ageLabel) {
    if (!dob || !ageLabel) return null;
    const d = new Date(dob);
    if (Number.isNaN(d.getTime())) return null;
    if (ageLabel.includes('At Birth') || ageLabel.includes('Newborn')) {
        return toMidnight(d);
    }
    const match = ageLabel.match(/(\d+)/);
    if (!match) return null;
    const n = parseInt(match[1], 10);
    const months = ageLabel.includes('Year') ? n * 12 : n;
    return addMonthsClamped(d, months);
}

function addMonthsClamped(source, months) {
    const target = new Date(source.getFullYear(), source.getMonth() + months, 1);
    const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
    return new Date(
        target.getFullYear(),
        target.getMonth(),
        Math.min(source.getDate(), lastDay),
    );
}

/** Human-readable label shown next to the colour in the table. */
export const VACCINE_STATUS_LABELS = {
    [VACCINE_STATUS.ACTION]: 'Action',
    [VACCINE_STATUS.UPCOMING]: 'Upcoming',
    [VACCINE_STATUS.DUE]: 'Due',
    [VACCINE_STATUS.OVERDUE]: 'Overdue',
    [VACCINE_STATUS.COMPLETED]: 'Completed',
};